import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import Router from "./routes/router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import CreatePostPopup from "./components/posts/CreatePostPopup";
import { useSelector } from "react-redux";
import toast, { Toaster, ToastBar } from "react-hot-toast";
import ErrorBoundary from "./components/ErrorBoundary";
import { useBranding } from "./hooks/useBranding";
import { useSessionBootstrap } from "./hooks/useSessionBootstrap";
import {
  useCtaTracking,
  usePageViewTracking,
  useUtmCapture,
} from "./hooks/useAnalytics";
import { LanguageProvider } from "./context/LanguageContext";
import ConfirmDialogHost from "./components/shared/ConfirmDialogHost";
import ToastPauseGuard, { TOASTER_CLASS, TOASTER_OPTIONS } from "./components/shared/ToastPauseGuard";
import AppUpdatePrompt from "./components/shared/AppUpdatePrompt";

export const queryClient = new QueryClient();

function BrandingLoader() {
  useBranding();
  // Bootstrap the signed-in session at app boot so even pages
  // outside the legacy NotLoggedIn wrapper (e.g. the marketing
  // landing) can render auth-aware CTAs.
  useSessionBootstrap();
  return null;
}

/**
 * Sits inside BrowserRouter so it can hook into route changes.
 * Fires GTM page_view on every navigation and captures UTM /
 * referrer / landing-page context once at first touch.
 */
function AnalyticsLoader() {
  usePageViewTracking();
  useUtmCapture();
  // Delegated cta_click handler — picks up clicks on any element
  // annotated with data-track="<cta_id>" anywhere in the React tree.
  useCtaTracking();
  return null;
}

function ClanExperienceClassLoader() {
  const location = useLocation();
  const authUser = useSelector((state) => state.user?.userinfo);

  useEffect(() => {
    const pathname = location.pathname || "/";
    const isAuthenticated = Boolean(
      authUser?._id ||
        authUser?.guid ||
        authUser?.username ||
        authUser?.email ||
        authUser?.mobile
    );
    const isPlatformAdministration =
      pathname === "/app" || pathname.startsWith("/app/");
    const isOnboardingChat =
      pathname === "/onboarding/chat" ||
      pathname.startsWith("/onboarding/chat/") ||
      /^\/[^/]+\/onboarding\/chat(?:\/|$)/.test(pathname);
    const isAuthOrPublicCommerce =
      pathname === "/login" ||
      pathname === "/signup" ||
      pathname === "/forgot" ||
      pathname.startsWith("/reset-password") ||
      pathname.startsWith("/get-started") ||
      pathname.startsWith("/onboarding/verify") ||
      isOnboardingChat ||
      pathname.startsWith("/site/");
    const normalizedAdminPath = pathname.replace(
      /^\/[^/]+(?=\/(?:administrator|admin)(?:\/|$))/,
      ""
    );
    const isClanAdmin =
      normalizedAdminPath === "/administrator" ||
      normalizedAdminPath.startsWith("/administrator/") ||
      normalizedAdminPath === "/admin" ||
      normalizedAdminPath.startsWith("/admin/");
    const isClanExperience =
      isAuthenticated && !isPlatformAdministration && !isAuthOrPublicCommerce;
    const classTargets = [document.documentElement, document.body].filter(
      Boolean
    );

    classTargets.forEach((target) => {
      target.classList.toggle("uniflo-clan-experience", isClanExperience);
      target.classList.toggle(
        "uniflo-clan-admin-experience",
        isClanExperience && isClanAdmin
      );
      target.classList.toggle(
        "uniflo-clan-workspace-experience",
        isClanExperience && !isClanAdmin
      );
    });

    return () => {
      classTargets.forEach((target) => {
        target.classList.remove(
          "uniflo-clan-experience",
          "uniflo-clan-admin-experience",
          "uniflo-clan-workspace-experience"
        );
      });
    };
  }, [authUser, location.pathname]);

  return null;
}

function App() {
  const user = useSelector((state) => ({ ...state.user.userinfo }));
  const createPost = useSelector((state) => state.createPost);
  const theme = useSelector((state) => state.user.theme || "light");

  useEffect(() => {
    if (createPost.isOpen) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  }, [createPost.isOpen]);

  useEffect(() => {
    const isDarkTheme = theme === "dark";
    document.documentElement.classList.toggle("dark", isDarkTheme);
    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
    if (document.body) {
      document.body.classList.toggle("dark", isDarkTheme);
      document.body.dataset.theme = theme;
    }
  }, [theme]);

  return (
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary>
        <LanguageProvider>
          <BrandingLoader />
          <AnalyticsLoader />
          <ClanExperienceClassLoader />
          {createPost.isOpen && <CreatePostPopup user={user} />}
            {/* Rendered inside #root (not portaled to document.body): in
              React 17+ event delegation is bound to the root container, so a
              toast portaled outside #root never receives onClick — which is
              why the dismiss (X) on custom notification toasts did nothing.
              The Toaster uses position:fixed, so it still overlays correctly. */}
            <Toaster
              position="bottom-left"
              reverseOrder={false}
              containerClassName={TOASTER_CLASS}
              toastOptions={TOASTER_OPTIONS}
            >
              {(t) => (
                <ToastBar toast={t}>
                  {({ icon, message }) => (
                    <>
                      {icon}
                      {message}
                      {t.type !== "loading" && (
                        <button
                          type="button"
                          onClick={() => toast.dismiss(t.id)}
                          aria-label="Dismiss notification"
                          style={{
                            marginLeft: 8,
                            padding: "0 6px",
                            background: "transparent",
                            border: 0,
                            color: "inherit",
                            fontSize: 18,
                            lineHeight: 1,
                            opacity: 0.6,
                            cursor: "pointer",
                          }}
                        >
                          ×
                        </button>
                      )}
                    </>
                  )}
                </ToastBar>
              )}
            </Toaster>
            {/* Ends the library's hover pause when a toast is closed under the
              pointer; without it no later toast ever times out. */}
            <ToastPauseGuard />
          <ConfirmDialogHost />
          <AppUpdatePrompt />
          <Router />
        </LanguageProvider>
      </ErrorBoundary>
    </QueryClientProvider>
  );
}

export default App;
